import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { API_URL } from '@/lib/api';
import { useToast } from './use-toast';

export function useEvents(options?: { onEvent?: (event: any) => void }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [isConnected, setIsConnected] = useState(false);
  const esRef = useRef<EventSource | null>(null);
  // Keep stable reference to options to avoid reconnecting the SSE on every render
  const optionsRef = useRef(options);
  useEffect(() => { optionsRef.current = options; });

  useEffect(() => {
    // Construct SSE URL - ensure we pass credentials for auth
    const url = `${API_URL}/events`;
    
    console.log('[SSE] Connecting to', url);
    const es = new EventSource(url, { withCredentials: true });
    esRef.current = es;

    es.onopen = () => {
      console.log('[SSE] Connection opened');
      setIsConnected(true);
    };

    es.onerror = (err) => {
      console.error('[SSE] Connection error', err);
      setIsConnected(false);
    };

    // Generic event handler for all system events
    const handleEvent = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        console.log("[SSE] Received event:", data);

        if (data.type === "ORDER_UPDATED" || data.type === "ORDER_CREATED") {
          qc.invalidateQueries({ queryKey: ["orders"] });
          if (data.payload?.id) {
            qc.invalidateQueries({ queryKey: ["orders", data.payload.id] });
            qc.invalidateQueries({ queryKey: ["orderSteps", data.payload.id] });
          }
        }

        if (data.type === "PAYMENT_RECORDED") {
          qc.invalidateQueries({ queryKey: ["orders"] });
          qc.invalidateQueries({ queryKey: ["payments"] });
          if (data.payload?.invoiceId) {
            qc.invalidateQueries({
              queryKey: ["orders", data.payload.invoiceId],
            });
          }
        }

        if (
          data.type === "STOCK_LEVEL_UPDATED" ||
          data.type === "INVENTORY_LOW"
        ) {
          qc.invalidateQueries({ queryKey: ["stockItems"] });
        }

        if (data.type === "ALERT_CREATED" || data.type === "INVENTORY_LOW") {
          qc.invalidateQueries({ queryKey: ["alerts"] });
          toast({
            title: data.payload?.title || "Inventory Alert",
            description:
              data.payload?.message || "Stock level critical for some items",
            variant: "destructive",
          });
        }
        
        // Call caller's onEvent via stable ref (avoids stale closure)
        if (optionsRef.current?.onEvent) {
          optionsRef.current.onEvent(data);
        }
      } catch (e) {
        console.error("[SSE] Failed to parse event data", e);
      }
    };

    // Listen for events
    es.addEventListener("ORDER_UPDATED", handleEvent);
    es.addEventListener("ORDER_CREATED", handleEvent);
    es.addEventListener("PAYMENT_RECORDED", handleEvent);
    es.addEventListener("ALERT_CREATED", handleEvent);
    es.addEventListener("STOCK_LEVEL_UPDATED", handleEvent);
    es.addEventListener("INVENTORY_LOW", handleEvent);

    return () => {
      console.log('[SSE] Closing connection');
      es.close();
      esRef.current = null;
    };
  }, [qc, toast]);

  return { isConnected };
}
